/**
 * Audit Logs column catalog, defaults, and localStorage preference helpers.
 */
import type { ColumnSelectOption } from '@/components/ColumnSelectModal';
import type { AuditLogRow } from './mapAuditEventToRow';

export const AUDIT_LOGS_COLUMNS_STORAGE_KEY = 'audit-logs-columns';

export const AUDIT_LOG_CELL_TRUNCATE_AT = 120;

export interface AuditLogColumnDef {
  key: keyof AuditLogRow;
  label: string;
  defaultVisible: boolean;
  sensitive: boolean;
}

/** Exhaustive selectable columns for the Audit Logs table. */
export const AUDIT_LOG_COLUMN_CATALOG: readonly AuditLogColumnDef[] = [
  { key: 'id', label: 'ID', defaultVisible: false, sensitive: false },
  { key: 'lastUpdated', label: 'Last Updated', defaultVisible: false, sensitive: false },
  { key: 'versionId', label: 'Version ID', defaultVisible: false, sensitive: false },
  { key: 'profile', label: 'Profile', defaultVisible: false, sensitive: false },
  { key: 'security', label: 'Security', defaultVisible: false, sensitive: false },
  { key: 'tag', label: 'Tag', defaultVisible: false, sensitive: false },
  { key: 'compartment', label: 'Compartment', defaultVisible: false, sensitive: false },
  { key: 'project', label: 'Project', defaultVisible: false, sensitive: false },
  { key: 'recorded', label: 'Recorded', defaultVisible: true, sensitive: false },
  { key: 'eventDisplay', label: 'Event', defaultVisible: true, sensitive: false },
  { key: 'type', label: 'Type', defaultVisible: false, sensitive: false },
  { key: 'subtype', label: 'Subtype', defaultVisible: false, sensitive: false },
  { key: 'action', label: 'Action', defaultVisible: false, sensitive: false },
  { key: 'period', label: 'Period', defaultVisible: false, sensitive: false },
  { key: 'outcome', label: 'Outcome', defaultVisible: true, sensitive: false },
  { key: 'outcomeDesc', label: 'Outcome Description', defaultVisible: false, sensitive: true },
  { key: 'purposeOfEvent', label: 'Purpose Of Event', defaultVisible: false, sensitive: false },
  { key: 'agentDisplay', label: 'Agent', defaultVisible: true, sensitive: false },
  { key: 'agentWho', label: 'Agent Who', defaultVisible: false, sensitive: false },
  { key: 'agentName', label: 'Agent Name', defaultVisible: false, sensitive: false },
  { key: 'agentType', label: 'Agent Type', defaultVisible: false, sensitive: false },
  { key: 'agentRole', label: 'Agent Role', defaultVisible: false, sensitive: false },
  { key: 'agentAltId', label: 'Agent Alt ID', defaultVisible: false, sensitive: false },
  { key: 'agentRequestor', label: 'Agent Requestor', defaultVisible: false, sensitive: false },
  { key: 'agentLocation', label: 'Agent Location', defaultVisible: false, sensitive: false },
  { key: 'agentPolicy', label: 'Agent Policy', defaultVisible: false, sensitive: false },
  { key: 'agentMedia', label: 'Agent Media', defaultVisible: false, sensitive: false },
  { key: 'agentPurposeOfUse', label: 'Agent Purpose Of Use', defaultVisible: false, sensitive: false },
  { key: 'agentNetworkAddress', label: 'Agent Network Address', defaultVisible: false, sensitive: true },
  { key: 'agentNetworkType', label: 'Agent Network Type', defaultVisible: false, sensitive: false },
  { key: 'entitySummary', label: 'Entity', defaultVisible: true, sensitive: false },
  { key: 'entityWhat', label: 'Entity What', defaultVisible: false, sensitive: false },
  { key: 'entityType', label: 'Entity Type', defaultVisible: false, sensitive: false },
  { key: 'entityRole', label: 'Entity Role', defaultVisible: false, sensitive: false },
  { key: 'entityLifecycle', label: 'Entity Lifecycle', defaultVisible: false, sensitive: false },
  { key: 'entitySecurityLabel', label: 'Entity Security Label', defaultVisible: false, sensitive: false },
  { key: 'entityName', label: 'Entity Name', defaultVisible: false, sensitive: true },
  { key: 'entityDescription', label: 'Entity Description', defaultVisible: false, sensitive: true },
  { key: 'entityQuery', label: 'Entity Query', defaultVisible: false, sensitive: true },
  { key: 'entityDetail', label: 'Entity Detail', defaultVisible: false, sensitive: true },
  { key: 'sourceSite', label: 'Source Site', defaultVisible: false, sensitive: false },
  { key: 'sourceObserver', label: 'Source Observer', defaultVisible: false, sensitive: false },
  { key: 'sourceType', label: 'Source Type', defaultVisible: false, sensitive: false },
] as const;

export const AUDIT_LOG_DEFAULT_COLUMN_KEYS: string[] = AUDIT_LOG_COLUMN_CATALOG.filter(
  (c) => c.defaultVisible,
).map((c) => c.key);

const CATALOG_KEY_SET = new Set(AUDIT_LOG_COLUMN_CATALOG.map((c) => c.key));

export function auditLogColumnOptions(): ColumnSelectOption[] {
  return AUDIT_LOG_COLUMN_CATALOG.map((c) => ({
    key: c.key,
    displayLabel: c.label,
  }));
}

/** Truncate for table cells; full value stays available via title/aria-label. */
export function truncateAuditLogCell(value: string): { display: string; full: string } {
  const full = value || '—';
  if (full.length <= AUDIT_LOG_CELL_TRUNCATE_AT) {
    return { display: full, full };
  }
  return { display: `${full.slice(0, AUDIT_LOG_CELL_TRUNCATE_AT)}…`, full };
}

/** Validate stored preference → ordered catalog keys, or null for defaults. */
export function parseAuditLogColumnPreference(raw: string | null): string[] | null {
  if (raw == null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return null;
    const seen = new Set<string>();
    const keys: string[] = [];
    for (const item of parsed) {
      if (typeof item !== 'string') continue;
      if (!CATALOG_KEY_SET.has(item as keyof AuditLogRow)) continue;
      if (seen.has(item)) continue;
      seen.add(item);
      keys.push(item);
    }
    return keys.length > 0 ? keys : null;
  } catch {
    return null;
  }
}

export function loadAuditLogColumnPreference(): string[] | null {
  if (typeof localStorage === 'undefined') return null;
  try {
    return parseAuditLogColumnPreference(localStorage.getItem(AUDIT_LOGS_COLUMNS_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function saveAuditLogColumnPreference(value: string[] | null): void {
  if (typeof localStorage === 'undefined') return;
  try {
    if (value == null || value.length === 0) {
      localStorage.removeItem(AUDIT_LOGS_COLUMNS_STORAGE_KEY);
    } else {
      localStorage.setItem(AUDIT_LOGS_COLUMNS_STORAGE_KEY, JSON.stringify(value));
    }
  } catch {
    // Ignore quota / private-mode failures.
  }
}
